export const onboardingEnTranslations = {
  title: 'Welcome to Intera',
  description: "Let's get you set up. Please fill out the form below.",
  firstName: 'First Name',
  lastName: 'Last Name',
  imageUrl: 'Image URL',
  submit: 'Submit',
  submitting: 'Submitting...',
  onboardingFailed: 'Onboarding failed',
  onboardingWelcome: 'Press to start',
  selectTeam: 'Select Your Team',
  selectTeamDescription: "Choose the team you'd like to join",
  noTeamsAvailable: 'No teams available',
  continue: 'Continue',
  loadingTeams: 'Loading teams...',
  searchPlaceholder: 'Search teams by name or description...',
  noSearchResults: 'No teams found matching your search',
  completeWithoutTeam: 'Complete Onboarding Without Team',
  preparing: 'Preparing your workspace…',
  admin: {
    step: {
      integrations: 'Connect',
      processing: 'Processing',
    },
    integrations: {
      stepBadge: 'Integrations',
      heading: "Let's connect your company's tools",
      subheading:
        'Intera reads your tools to learn how your company actually operates. The more you connect, the sharper your team’s insights.',
      connectedCount_zero: 'Nothing connected yet',
      connectedCount_one: '{{count}} tool connected',
      connectedCount_other: '{{count}} tools connected',
      connect: 'Connect',
      connecting: 'Connecting…',
      connected: 'Connected',
      comingSoon: 'Coming soon',
      comingSoonHint: 'Available soon',
      continue: 'Continue',
      softGateHint: 'Connect at least one tool to continue.',
      nudgeMore: 'You can connect more later — continue any time.',
      providers: {
        sharepoint: {
          name: 'Microsoft SharePoint',
          description: 'Index docs, PRDs, and knowledge bases.',
        },
        dynamicsCrm: {
          name: 'Microsoft Dynamics CRM',
          description: 'Read accounts, opportunities, and contacts.',
        },
        jira: {
          name: 'Jira',
          description: 'Read issues, projects, and comments.',
        },
        confluence: {
          name: 'Confluence',
          description: 'Read pages and spaces.',
        },
        slack: {
          name: 'Slack',
          description: 'Channels, threads, and DMs (read-only).',
        },
        salesforce: {
          name: 'Salesforce',
          description: 'Accounts, opportunities, and pipeline.',
        },
        microsoftTeams: {
          name: 'Microsoft Teams',
          description: 'Channels, calls, and team chats.',
        },
        googleDrive: {
          name: 'Google Drive',
          description: 'Files, docs, and shared folders.',
        },
        notion: {
          name: 'Notion',
          description: 'Pages, databases, and wikis.',
        },
        github: {
          name: 'GitHub',
          description: 'Repos, pull requests, and issues.',
        },
        hubspot: {
          name: 'HubSpot',
          description: 'Contacts, deals, and pipeline.',
        },
        asana: {
          name: 'Asana',
          description: 'Projects, tasks, and timelines.',
        },
        linear: {
          name: 'Linear',
          description: 'Issues, projects, and cycles.',
        },
        zoom: {
          name: 'Zoom',
          description: 'Meetings and recordings.',
        },
        zendesk: {
          name: 'Zendesk',
          description: 'Tickets and customer threads.',
        },
        monday: {
          name: 'monday.com',
          description: 'Boards, tasks, and workflows.',
        },
      },
    },
    processing: {
      headline: 'You can close this. Intera is now doing its part.',
      subheading: 'We’ll surface what we find the next time you log in.',
      manageIntegrations: 'Add more integrations',
    },
  },
  holding: {
    heading: 'Sit tight. Your team is still getting set up.',
    subheading:
      "Your team hasn't connected its tools yet. We'll let you know when there's something to show you.",
    continue: 'Continue to dashboard',
  },

  // Get-started gate — shown before a page that requires a completed account
  getStarted: {
    title: 'Welcome to INTERA-AI',
    cta: 'Start using Intera',
    starting: 'Setting up your account…',
    failed: 'We could not finish setting up your account.',
  },

  // Insights flow
  insights: {
    welcome: {
      eyebrow: 'Your first look',
      title: "We've already read your world.",
      subtitle: "Here's what stands out.",
      cta: 'Show me',
    },
    progress: {
      step: (current: number, total: number) => `${current} of ${total}`,
    },
    pastLesson: {
      eyebrow: 'Already happened',
      title: ({ cost }: { cost: string }) =>
        `This same gap cost you ${cost} last quarter.`,
      subtitle:
        "Your plan said one thing. The actual ship did another. Here's the lesson, so it doesn't repeat.",
      planLabel: 'Plan',
      actualLabel: 'Actual',
      lateBadge: ({ cost }: { cost: string }) => `${cost} late`,
      rootCauseLabel: 'Root cause:',
    },
    misalignment: {
      eyebrow: 'Misalignment detected',
      title: 'Two artifacts are telling different stories.',
      sourcesLabel: 'Conflicting sources',
      contradictionLabel: 'Directly contradicts',
      dismiss: 'Dismiss',
      snooze: 'Snooze',
    },
    procedureSlipped: {
      eyebrow: 'Procedure slipped',
      title: "We've learned how your company works, and caught a miss.",
      ruleLabel: 'Inferred lesson',
      missLabel: 'Active anomaly',
      sourcesLabel: 'Where to verify',
      dismiss: 'Dismiss',
      snooze: 'Snooze',
    },
    done: {
      eyebrow: 'You are set up',
      title: 'Intera is now working in the background.',
      subtitle: 'New insights will surface as your team works.',
      cta: 'Take me to my dashboard',
    },
    coldStart: {
      eyebrow: 'We are still learning',
      title: 'Intera is reading your world right now.',
      subtitle:
        "We don't have enough signal yet to surface insights. Connect more tools to speed things up.",
      processedHeading: 'What we have processed so far',
      counts: {
        sharepointFiles: (count: number) => `${count} SharePoint files indexed`,
        jiraIssues: (count: number) => `${count} Jira issues read`,
        confluencePages: (count: number) => `${count} Confluence pages indexed`,
        crmAccounts: (count: number) => `${count} CRM accounts linked`,
      },
      connectCta: 'Connect more integrations',
      finishCta: 'Take me to my dashboard',
    },
    feedback: {
      prompt: 'Was this useful?',
      like: 'Helpful',
      dislike: 'Not helpful',
      continueHint: 'Pick one to continue',
    },
    sourceProvider: {
      slack: 'Slack',
      jira: 'Jira',
      confluence: 'Confluence',
      'dynamics-crm': 'Dynamics CRM',
      sharepoint: 'SharePoint',
    },
    openSource: 'Open',
    completeFailed: 'We could not finish setting up your account.',
  },
};
